import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';
import tracking from 'dibs-tracking';

// TODO Shanie: FINDING-14024 - Remove "Old" components post A/B Test
import { HpRespHeroModuleOld } from './HpRespHeroModuleOld/HpRespHeroModuleOld';
import { HpRespTopHeroBannerModule } from './HpRespTopHeroBannerModule/HpRespTopHeroBannerModule';
import { HpRespRecentlyViewedItemsLoader } from './HpRespRecentlyViewedItems/HpRespRecentlyViewedItemsLoader';
import { HpRespLocationsModule } from './HpRespLocationsModule/HpRespLocationsModule';
import { HpRespPersonalizedCategoriesLoader } from './HpRespPersonalizedCategories/HpRespPersonalizedCategoriesLoader';
import HpRespPromoModulePrimary from './HpRespPromoModule/HpRespPromoModulePrimary';
import HpRespPromoModuleSecondary from './HpRespPromoModule/HpRespPromoModuleSecondary';
import { HpRespCollectionsModuleOld } from './HpRespCollectionsModuleOld/HpRespCollectionsModuleOld';
import { HpRespFeaturedModule } from './HpRespFeaturedModule/HpRespFeaturedModule';
import HpRespEditorialModule from './HpRespEditorialModule/HpRespEditorialModule';
import { HpRespShopByModule } from './HpRespShopByModule/HpRespShopByModule';
import HpRespSkinnyBannerModule from './HpRespSkinnyBannerModule/HpRespSkinnyBannerModule';
import { HomePageContext } from './HomePageContext';
import { ShopLocalNotificationLoader } from '../notifications/shopLocal/ShopLocalNotificationLoader';
import { connectContext } from '../context/helpers/connectContext';
import { getItemsPerPage } from './helpers/getItemsPerPage';
import getLazyLoadStartingPoint from './helpers/getLazyLoadStartingPoint';
import { LOCATION_ITEM_PAGE_COUNT } from './constants';
import {
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-buyer-layout/exports/regionalInfoHelpers';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';

/* eslint-disable react/prop-types */
const moduleComponents = {
    skinnyBanner: ({ componentModule, key, useLazyLoadImages }) => (
        <HpRespSkinnyBannerModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
        />
    ),
    topHeroBanner: ({ componentModule, key, useLazyLoadImages, isMobile }) => (
        <HpRespTopHeroBannerModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
        />
    ),
    hero: ({ componentModule, key, useLazyLoadImages }) => (
        <HpRespHeroModuleOld
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
        />
    ),
    collections: ({ componentModule, key, useLazyLoadImages }) => (
        <HpRespCollectionsModuleOld
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
        />
    ),
    promoPrimary: ({ componentModule, key, useLazyLoadImages, isMobile }) => (
        <HpRespPromoModulePrimary
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
        />
    ),
    promoSecondary: ({
        componentModule,
        promoPrimaryItemsCount,
        key,
        useLazyLoadImages,
        isMobile,
    }) => (
        <HpRespPromoModuleSecondary
            key={key}
            componentModule={componentModule}
            promoPrimaryItemsCount={promoPrimaryItemsCount}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
        />
    ),
    featured: ({ componentModule, key, useLazyLoadImages }) => (
        <HpRespFeaturedModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
        />
    ),
    editorial: ({ componentModule, key, useLazyLoadImages }) => (
        <HpRespEditorialModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
        />
    ),
    shopBy: ({ componentModule, key, useLazyLoadImages }) => (
        <HpRespShopByModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
        />
    ),
    locations: ({ componentModule, viewer, key, itemsPerPage, isMobile, useLazyLoadImages }) => (
        <HpRespLocationsModule
            key={key}
            componentModule={componentModule}
            viewer={viewer || null}
            itemsPerPage={itemsPerPage}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
        />
    ),
    recentlyViewed: ({ viewer, isClient, isMobile, key, itemsPerPage, componentModule }) =>
        isClient && (
            <HpRespRecentlyViewedItemsLoader
                key={key}
                user={viewer?.user || null}
                itemsPerPage={itemsPerPage}
                showFullHeader
                additionalSpacing={isMobile}
                componentModule={componentModule}
            />
        ),
    arrivals: ({
        isClient,
        isMobile,
        showLoggedInModules,
        personalization,
        viewer,
        componentModule,
        key,
    }) =>
        isClient &&
        showLoggedInModules && (
            <HpRespPersonalizedCategoriesLoader
                key={key}
                personalization={personalization}
                viewer={viewer}
                hasBorder={!isMobile}
                hasNoPageGutter={isMobile}
                pageSetOffset={!isMobile}
                componentModule={componentModule}
            />
        ),
};

const HpRespLayoutComponent = props => {
    const [hasFiredPageViewTracking, setHasFiredPageViewTracking] = useState(false);
    const [userId, setUserId] = useState(null);
    const [isClient, setIsClient] = useState(null);

    const { relay, isLoggedInUser, viewer, isMobile, isTablet } = props;
    const itemsPerPage = getItemsPerPage({ isMobile, isTablet });

    const user = viewer?.user || null;

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: true });
            relay.refetch(
                fragmentVariables => ({
                    ...fragmentVariables,
                    fetchInitialHomepage: false,
                    isClient: true,
                    isMobile,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    userCountryCode: getUserSessionCountryCode(),
                    zipCode: getUserZipCode(),
                    localRecentHistoryItemIds,
                    fetchUserRecentHistoryItem:
                        localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
                    ...additionalVariables,
                    locationItemsCount: itemsPerPage * LOCATION_ITEM_PAGE_COUNT,
                }),
                { fetchInitialHomepage: true },
                () => setIsClient(true)
            );
        },
        [userId, relay, isMobile, itemsPerPage]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            setUserId(cookieUserId);
        }
    }, [userId]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        if (!hasFiredPageViewTracking) {
            setHasFiredPageViewTracking(true);
            tracking.trackPageview();
        }
        setUserData();
        addLoginHandlers(data => {
            if (data?.user?.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload(true);
                return;
            }
            setUserData();
        });
    }, [setUserData, hasFiredPageViewTracking, setHasFiredPageViewTracking]);

    const { homepage = {} } = viewer;

    const { personalization = null, modules } = homepage;
    const showLoggedInModules = isLoggedInUser || userId;
    const promoPrimaryModule = modules.find(mod => mod.type === 'promo' && mod.index === 0);
    const promoPrimaryItemsCount = promoPrimaryModule?.totalItemsCount || 0;
    const startLazyLoadAt = getLazyLoadStartingPoint({ isMobile, isTablet });

    const mods = modules.map((mod, index) => {
        let { type } = mod;

        if (type === 'promo') {
            type = mod.index === 0 ? 'promoPrimary' : 'promoSecondary';
        }

        const component = moduleComponents[type];
        if (component) {
            return component({
                componentModule: mod,
                key: `${type}-${index}`,
                promoPrimaryItemsCount,
                viewer,
                isMobile,
                isClient,
                itemsPerPage,
                showLoggedInModules,
                personalization,
                useLazyLoadImages: index >= startLazyLoadAt,
            });
        }
        return null;
    });

    return (
        <Fragment>
            {mods}
            <ShopLocalNotificationLoader environment={relay.environment} />
        </Fragment>
    );
};

HpRespLayoutComponent.propTypes = {
    isLoggedInUser: PropTypes.bool,
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
};

function mapContextToProps({ isLoggedInUser, isMobile, isTablet }) {
    return {
        isMobile,
        isTablet,
        isLoggedInUser,
    };
}

const HpRespLayoutWithContext = connectContext(
    HomePageContext,
    mapContextToProps
)(HpRespLayoutComponent);

export const HpRespLayout = createRefetchContainer(
    HpRespLayoutWithContext,
    {
        viewer: graphql`
            fragment HpRespLayout_viewer on Viewer {
                ...HpRespPersonalizedCategories_viewer
                homepage(rootId: $homepageId) {
                    modules {
                        ... on SkinnyBannerModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespSkinnyBannerModule_componentModule
                        }
                        ... on TopHeroBannerModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespTopHeroBannerModule_componentModule
                        }
                        ... on HeroModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespHeroModuleOld_componentModule
                        }
                        ... on CollectionsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespCollectionsModuleOld_componentModule
                        }
                        ... on FeaturedModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespFeaturedModule_componentModule
                        }
                        ... on PromoModule @include(if: $fetchInitialHomepage) {
                            totalItemsCount
                            index
                            type
                            ...HpRespPromoModulePrimary_componentModule
                            ...HpRespPromoModuleSecondary_componentModule
                        }
                        ... on EditorialModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespEditorialModule_componentModule
                        }
                        ... on ShopByModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespShopByModule_componentModule
                        }
                        ... on LocationsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespLocationsModule_componentModule
                        }
                        ... on ArrivalsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespPersonalizedCategories_componentModule
                        }
                        ... on RecentlyViewedModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpRespRecentlyViewedItems_componentModule
                        }
                    }
                    personalization(userId: $userId, first: 3, numItems: 3)
                        @include(if: $hasUserId) {
                        ...HpRespPersonalizedCategories_personalization
                    }
                }
                ...HpRespLocationsModule_viewer
                user(userId: $userId) @include(if: $hasUserId) {
                    ...HpRespRecentlyViewedItems_user
                }
            }
        `,
    },
    graphql`
        query HpRespLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $isClient: Boolean!
            $isMobile: Boolean!
            $fetchInitialHomepage: Boolean!
            $zipCode: String
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
            $locationItemsCount: Int
        ) {
            viewer {
                ...HpRespLayout_viewer
            }
        }
    `
);
